import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { TruncatedText } from '@/features/shared/components/ui/TruncatedText';
import { Input } from '@/features/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/features/shared/components/ui/dialog';
import {
  Search, Bot, Mail, FileText, BookOpen, CalendarDays, UserCircle, Wallet,
  ArrowRight, ExternalLink, Sparkles, Lock,
} from 'lucide-react';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';

// Open Dana (the floating HR copilot) from anywhere, optionally with a question.
const askDana = (question?: string) =>
  window.dispatchEvent(new CustomEvent('dana:ask', { detail: { question } }));

const SUPPORT_EMAIL = 'support@kazipeople.com';

type Cap = 'admin' | 'manager' | 'hr';
interface ArticleMeta { id: string; cat: CatKey; to: string; cap?: Cap }
type CatKey = 'getting_started' | 'time_off' | 'profile' | 'payroll';

// Text lives in i18n (en/sw); this holds only the stable wiring (id → category,
// destination page, and the access level needed to use that page).
const ARTICLES: ArticleMeta[] = [
  { id: 'gs-setup', cat: 'getting_started', to: 'admin/setup', cap: 'admin' },
  { id: 'gs-nav', cat: 'getting_started', to: 'dashboard' },
  { id: 'gs-profile', cat: 'getting_started', to: 'personal' },
  { id: 'to-request', cat: 'time_off', to: 'time-off' },
  { id: 'to-balance', cat: 'time_off', to: 'time-off' },
  { id: 'to-approve', cat: 'time_off', to: 'leave-approvals', cap: 'manager' },
  { id: 'to-calendar', cat: 'time_off', to: 'my-calendar' },
  { id: 'pp-update', cat: 'profile', to: 'personal' },
  { id: 'pp-emergency', cat: 'profile', to: 'personal' },
  { id: 'pp-docs', cat: 'profile', to: 'documents' },
  { id: 'pb-payslips', cat: 'payroll', to: 'my-pay' },
  { id: 'pb-policies', cat: 'payroll', to: 'documents' },
  { id: 'pb-salary', cat: 'payroll', to: 'employees', cap: 'hr' },
];

const CAT_META: { key: CatKey; icon: React.ComponentType<any> }[] = [
  { key: 'getting_started', icon: BookOpen },
  { key: 'time_off', icon: CalendarDays },
  { key: 'profile', icon: UserCircle },
  { key: 'payroll', icon: Wallet },
];

export const HelpPage: React.FC = () => {
  const { t } = useTranslation('help');
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const go = (to: string) => navigate(tenantSlug ? `/t/${tenantSlug}/app/${to}` : `/app/${to}`);

  const { isTenantOwner, isTenantAdmin, isManager, isHrManager } = useRoleAccess();
  const hasCap = (cap?: Cap) => {
    if (!cap) return true;
    if (cap === 'manager') return isManager;        // managers, HR, owners
    if (cap === 'admin') return isTenantAdmin;       // owner / company admin
    if (cap === 'hr') return isTenantOwner || isHrManager;
    return true;
  };

  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const active = ARTICLES.find((a) => a.id === activeId) || null;

  // Pull translated fields for an article id.
  const A = (id: string) => ({
    title: t(`articles.${id}.title`),
    summary: t(`articles.${id}.summary`),
    steps: t(`articles.${id}.steps`, { returnObjects: true }) as string[],
    link: t(`articles.${id}.link`),
    ask: t(`articles.${id}.ask`),
  });

  const q = query.trim().toLowerCase();
  const matchArticle = (m: ArticleMeta) => {
    if (!q) return true;
    const a = A(m.id);
    return [a.title, a.summary, ...(Array.isArray(a.steps) ? a.steps : [])]
      .some((s) => String(s).toLowerCase().includes(q));
  };
  const filteredArticles = useMemo(() => ARTICLES.filter(matchArticle), [q]);

  const faqs = (t('faqs', { returnObjects: true }) as { q: string; a: string }[]) || [];
  const filteredFaqs = useMemo(
    () => faqs.filter((f) => !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)),
    [q, faqs],
  );

  const resultsLabel = (n: number) =>
    n === 1 ? t('results_one', { count: n, q: query.trim() }) : t('results_other', { count: n, q: query.trim() });

  const capBadge = (cap?: Cap) =>
    cap ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground whitespace-nowrap">{t('for_label', { who: t(`roles.${cap}`) })}</span> : null;

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-6">
        <h1 className="text-h1 bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{t('title')}</h1>
        <p className="text-muted-foreground mt-1">{t('subtitle')}</p>
      </div>

      {/* Search + Ask Dana */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-400 w-5 h-5" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('search_placeholder')}
              className="pl-10 h-12 text-lg border-orange-200 dark:border-orange-500/25 focus:border-orange-400 focus:ring-orange-200" />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={() => askDana(query.trim() || t('contact.dana.prompt'))}
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0 gap-2">
              <Sparkles className="w-4 h-4" /> {q ? t('ask_dana_query', { q: query.trim() }) : t('ask_dana_help')}
            </Button>
            <span className="text-sm text-muted-foreground">{t('ask_tagline')}</span>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="categories" className="space-y-6">
        <TabsList className="bg-orange-50 dark:bg-orange-500/[0.08] border-orange-200 dark:border-orange-500/25">
          <TabsTrigger value="categories" className="data-[state=active]:bg-card data-[state=active]:text-orange-600">{t('tabs.categories')}</TabsTrigger>
          <TabsTrigger value="faq" className="data-[state=active]:bg-card data-[state=active]:text-orange-600">{t('tabs.faq')}</TabsTrigger>
          <TabsTrigger value="contact" className="data-[state=active]:bg-card data-[state=active]:text-orange-600">{t('tabs.contact')}</TabsTrigger>
          <TabsTrigger value="resources" className="data-[state=active]:bg-card data-[state=active]:text-orange-600">{t('tabs.resources')}</TabsTrigger>
        </TabsList>

        {/* Categories */}
        <TabsContent value="categories" className="space-y-6">
          {q ? (
            <Card>
              <CardHeader><CardTitle className="text-foreground">{resultsLabel(filteredArticles.length)}</CardTitle></CardHeader>
              <CardContent>
                {filteredArticles.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('no_articles')} <button className="text-orange-600 hover:underline" onClick={() => askDana(query.trim())}>{t('ask_instead')}</button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredArticles.map((m) => {
                      const a = A(m.id);
                      return (
                        <button key={m.id} onClick={() => setActiveId(m.id)}
                          className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-border hover:border-orange-300 dark:hover:border-orange-500/40 hover:bg-orange-50 dark:hover:bg-orange-500/[0.08] transition-colors group">
                          <FileText className="w-4 h-4 text-orange-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-foreground group-hover:text-orange-600 flex items-center gap-2">{a.title} {capBadge(m.cap)}</div>
                            <TruncatedText className="text-sm text-muted-foreground">{a.summary}</TruncatedText>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-orange-600 shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {CAT_META.map((cat) => {
                const items = ARTICLES.filter((a) => a.cat === cat.key);
                return (
                  <Card key={cat.key} className="border-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-foreground">
                        <cat.icon className="w-5 h-5 text-orange-500" /> {t(`categories.${cat.key}.title`)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">{t(`categories.${cat.key}.desc`)}</p>
                      <div className="space-y-1">
                        {items.map((m) => (
                          <button key={m.id} onClick={() => setActiveId(m.id)}
                            className="w-full text-left text-sm text-orange-600 hover:text-orange-700 hover:underline flex items-center gap-2 py-1">
                            <span>• {t(`articles.${m.id}.title`)}</span> {capBadge(m.cap)}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* FAQ */}
        <TabsContent value="faq" className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-xl font-bold text-foreground">{t('tabs.faq')}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredFaqs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('no_faqs')} <button className="text-orange-600 hover:underline" onClick={() => askDana(query.trim())}>{t('ask_instead')}</button>
                  </div>
                ) : filteredFaqs.map((item, index) => (
                  <div key={index} className="border-b border-border pb-4 last:border-b-0">
                    <h3 className="font-medium text-foreground mb-2">{item.q}</h3>
                    <p className="text-muted-foreground">{item.a}</p>
                    <button onClick={() => askDana(item.q)} className="mt-2 text-sm text-orange-600 hover:underline inline-flex items-center gap-1">
                      <Bot className="w-3.5 h-3.5" /> {t('ask_about_this')}
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact */}
        <TabsContent value="contact" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-border">
              <CardHeader><CardTitle className="flex items-center gap-2 text-foreground"><Bot className="w-5 h-5 text-orange-500" /> {t('contact.dana.title')}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">{t('contact.dana.desc')}</p>
                <p className="text-sm text-muted-foreground mb-4">{t('contact.dana.meta')}</p>
                <Button onClick={() => askDana(t('contact.dana.prompt'))} className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white border-0">{t('contact.dana.cta')}</Button>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader><CardTitle className="flex items-center gap-2 text-foreground"><Mail className="w-5 h-5 text-orange-500" /> {t('contact.email.title')}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">{t('contact.email.desc')}</p>
                <p className="text-sm text-muted-foreground mb-4">{t('contact.email.meta')}</p>
                <a href={`mailto:${SUPPORT_EMAIL}?subject=KaziPeople%20support`}>
                  <Button className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white border-0">{t('contact.email.cta', { email: SUPPORT_EMAIL })}</Button>
                </a>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader><CardTitle className="flex items-center gap-2 text-foreground"><FileText className="w-5 h-5 text-orange-500" /> {t('contact.hr.title')}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">{t('contact.hr.desc')}</p>
                <p className="text-sm text-muted-foreground mb-4">{t('contact.hr.meta')}</p>
                <Button variant="outline" onClick={() => go('documents')} className="w-full">{t('contact.hr.cta')}</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Resources */}
        <TabsContent value="resources" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-border">
              <CardHeader><CardTitle className="flex items-center gap-2 text-foreground"><BookOpen className="w-5 h-5 text-orange-500" /> {t('resources.guides.title')}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">{t('resources.guides.desc')}</p>
                <div className="space-y-1">
                  {ARTICLES.slice(0, 5).map((m) => (
                    <button key={m.id} onClick={() => setActiveId(m.id)} className="block w-full text-left text-sm text-orange-600 hover:underline py-1">• {t(`articles.${m.id}.title`)}</button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader><CardTitle className="flex items-center gap-2 text-foreground"><ExternalLink className="w-5 h-5 text-orange-500" /> {t('resources.jump.title')}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">{t('resources.jump.desc')}</p>
                <div className="space-y-1">
                  {[
                    { to: 'documents', key: 'documents' },
                    { to: 'time-off', key: 'time_off' },
                    { to: 'my-pay', key: 'my_pay' },
                    { to: 'admin/setup', key: 'setup' },
                  ].map((r) => (
                    <button key={r.to} onClick={() => go(r.to)} className="block w-full text-left text-sm text-orange-600 hover:underline py-1">• {t(`resources.links.${r.key}`)}</button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Article detail */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActiveId(null)}>
        <DialogContent className="max-w-lg">
          {active && (() => {
            const a = A(active.id);
            const allowed = hasCap(active.cap);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-foreground">{a.title}</DialogTitle>
                  <DialogDescription>{a.summary}</DialogDescription>
                </DialogHeader>
                <ol className="space-y-3 my-2">
                  {(Array.isArray(a.steps) ? a.steps : []).map((s, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-500/[0.16] text-orange-700 dark:text-orange-300 text-sm font-semibold flex items-center justify-center">{i + 1}</span>
                      <span className="text-sm text-foreground pt-0.5">{s}</span>
                    </li>
                  ))}
                </ol>

                {/* Friendly message when this page is for a role the user isn't */}
                {!allowed && (
                  <div className="rounded-lg border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/[0.08] p-3 flex gap-3">
                    <Lock className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-orange-800 dark:text-orange-300">{t('no_access.title', { who: t(`roles.${active.cap}`) })}</div>
                      <p className="text-sm text-orange-700 dark:text-orange-400 mt-0.5">{t('no_access.message')}</p>
                    </div>
                  </div>
                )}

                <DialogFooter className="gap-2 sm:gap-2">
                  <Button variant="outline" onClick={() => { askDana(a.ask); setActiveId(null); }} className="gap-2">
                    <Bot className="w-4 h-4" /> {t('ask_dana')}
                  </Button>
                  {allowed ? (
                    <Button onClick={() => { const to = active.to; setActiveId(null); go(to); }}
                      className="bg-gradient-to-r from-orange-500 to-amber-600 text-white border-0 gap-2">
                      {a.link} <ArrowRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(a.title)}`}>
                      <Button className="bg-gradient-to-r from-orange-500 to-amber-600 text-white border-0 gap-2">
                        <Mail className="w-4 h-4" /> {t('no_access.ask_hr')}
                      </Button>
                    </a>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpPage;
